import { useState } from 'react';
import { Alert, Badge, Button, FileInput, Group, Stack, Table } from '@mantine/core';
import type { PersonImportReport } from '@isg/shared-types';
import { useCiktiIndir, usePersonelOnizleme, usePersonelYukle } from '../api/queries';
import { SATIR_SONUC_ETIKET } from '../i18n/tr';
import { BosListeNotu } from './parts';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * Toplu personel yükleme paneli (F1.5·S12) — TEK BİLEŞEN, İKİ BAĞLAM:
 * portalda işveren OTURUMDAN türetilir (satıra yazılmaz, sunucu türetir);
 * iç ekranda YÜKLENİCİ seçimlidir.
 *
 * ✏️ F4·S3 (madde 80): İŞVEREN ŞİRKETİ ARTIK SEÇİLMEZ — işyerinden TÜRETİLİR
 * ve işyeri satırın kendi kolonundan ("İşyeri SGK Sicil") gelir. Seçim
 * yalnızca *"bu kişiler ana firmanın mı, bir yüklenicinin mi personeli"*
 * sorusunu cevaplar; şirket seçtirmek aynı bilgiyi ikinci kez sormak ve
 * dosyayla çelişebilmek olurdu.
 *
 * Akış: şablon indir → dosya seç → önizleme → onayla → sonuç. **Akış aynı,
 * parse'ın yeri değişti.**
 *
 * ✏️ **F4·S9 (içe aktarma motoru — S6.5): ŞABLON ve PARSE SUNUCUDA.**
 * Önceden ikisi de tarayıcıdaydı (madde 20 öncesi geçici karar;
 * `lib/person-import-file.ts` kendi notunda *"dosya altyapısı gelince bu
 * modül düşer"* diyordu — düştü). Kolon sırası artık TEK yerde yaşar:
 * şablonu üreten ve dosyayı okuyan aynı tanımdır.
 *
 * Önizlemede kimlik no MASKELİ gösterilir — ama ekran maskelemez, **maske
 * API kenarındadır** (§2.16): ağdan inen önizleme yanıtı zaten maskeli gelir
 * (madde 37 dersi — ekranda maskelemek tiyatro olur). Satır İÇERİĞİ burada
 * doğrulanmaz: hata dili tek kaynaktan, sunucu raporundan gelir (portalda o
 * dil üç mesaja iner — D2).
 */
export function PersonelYuklemePaneli({
  contractorId,
}: {
  /**
   * Yüklenici personeli ise firma id'si; ana firma personelinde null
   * (portalda da null — sunucu oturumdan türetir ve gövdedeki değeri EZER).
   *
   * ⚠️ Şablonda işveren kolonu YOKTUR ve olmayacaktır: seçim burada,
   * istekte taşınır. F4·S9'da dosya sunucuya taşınınca bu değer multipart'ın
   * ayrı bir alanı oldu — satıra yazmanın yolu yine açılmadı.
   */
  contractorId: string | null;
}): React.JSX.Element {
  const yukleme = usePersonelYukle();
  const onizleme = usePersonelOnizleme();
  const indir = useCiktiIndir();
  const [dosya, setDosya] = useState<File | null>(null);

  function dosyaSecildi(secilen: File | null): void {
    setDosya(secilen);
    onizleme.reset();
    yukleme.reset();
    if (secilen) onizleme.mutate({ dosya: secilen });
  }

  function yukle(): void {
    if (dosya) yukleme.mutate({ dosya, contractorId });
  }

  return (
    <Stack gap="sm">
      <Group>
        <Button
          variant="default"
          size="xs"
          data-testid="sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: '/api/import/persons/template',
              yedekAd: 'personel-listesi-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="personel-dosya"
          placeholder="Doldurulmuş şablonu seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={dosyaSecildi}
          clearable
          size="xs"
          w={280}
        />
      </Group>
      {onizleme.isError && (
        <Alert color={ANLAM.engel} role="alert">
          {onizleme.error.message}
        </Alert>
      )}

      {onizleme.data && !yukleme.data && (
        <>
          {onizleme.data.satirlar.length === 0 ? (
            <BosListeNotu mesaj="Dosyada satır bulunamadı." />
          ) : (
            <>
              <Table striped data-testid="onizleme-tablosu">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Satır</Table.Th>
                    <Table.Th>Ad soyad</Table.Th>
                    <Table.Th>Kimlik</Table.Th>
                    <Table.Th>Görev</Table.Th>
                    <Table.Th>Başlangıç</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {onizleme.data.satirlar.map((satir) => (
                    <Table.Tr key={satir.satir} data-testid="onizleme-satiri">
                      <Table.Td>{satir.satir}</Table.Td>
                      <Table.Td>{satir.adSoyad}</Table.Td>
                      {/* Maskeli değer SUNUCUDAN gelir — ekran maskelemez. */}
                      <Table.Td>{satir.kimlikNoMaskeli}</Table.Td>
                      <Table.Td>{satir.rol}</Table.Td>
                      <Table.Td>{tarihGoster(satir.baslangic)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {yukleme.isError && (
                <Alert color={ANLAM.engel} role="alert">
                  {yukleme.error.message}
                </Alert>
              )}
              <Group>
                <Button data-testid="yukleme-onayla" loading={yukleme.isPending} onClick={yukle}>
                  Onayla ve yükle ({onizleme.data.satirlar.length} satır)
                </Button>
              </Group>
            </>
          )}
        </>
      )}

      {yukleme.data && (
        <SonucTablosu
          rapor={yukleme.data}
          onYeni={() => {
            setDosya(null);
            onizleme.reset();
            yukleme.reset();
          }}
        />
      )}
    </Stack>
  );
}

function SonucTablosu({
  rapor,
  onYeni,
}: {
  rapor: PersonImportReport;
  onYeni: () => void;
}): React.JSX.Element {
  const renk: Record<string, string> = {
    added: ANLAM.olumlu,
    new_employment: ANLAM.bilgi,
    unchanged: ANLAM.bilgi,
    restore_required: ANLAM.dikkat,
    error: ANLAM.engel,
  };
  const mevcut = rapor.sayilar.new_employment + rapor.sayilar.unchanged;
  return (
    <Stack gap="xs">
      <Group gap="xs" data-testid="sonuc-ozeti">
        <Badge color={ANLAM.olumlu} variant="light">
          Eklendi: {rapor.sayilar.added}
        </Badge>
        <Badge color={ANLAM.bilgi} variant="light">
          Mevcut: {mevcut}
        </Badge>
        {rapor.sayilar.restore_required > 0 && (
          <Badge color={ANLAM.dikkat} variant="light">
            {SATIR_SONUC_ETIKET.restore_required}: {rapor.sayilar.restore_required}
          </Badge>
        )}
        <Badge color={ANLAM.engel} variant="light">
          İşlenemedi: {rapor.sayilar.error}
        </Badge>
      </Group>
      <Table striped data-testid="sonuc-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Satır</Table.Th>
            <Table.Th>Sonuç</Table.Th>
            <Table.Th>Açıklama</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rapor.satirlar.map((satir) => (
            <Table.Tr key={satir.satir} data-testid="sonuc-satiri">
              <Table.Td>{satir.satir}</Table.Td>
              <Table.Td>
                <Badge color={renk[satir.sonuc] ?? ANLAM.bilgi} variant="light">
                  {SATIR_SONUC_ETIKET[satir.sonuc] ?? satir.sonuc}
                </Badge>
              </Table.Td>
              <Table.Td>{satir.mesaj ?? '—'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group>
        <Button variant="default" size="xs" data-testid="yeni-yukleme" onClick={onYeni}>
          Yeni yükleme
        </Button>
      </Group>
    </Stack>
  );
}
